import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Layout,
  Row,
  Col,
  Typography,
  Button,
  Rate,
  Spin,
  message,
  Space
} from 'antd';
import {
  AndroidOutlined,
  AppleOutlined,
  DownloadOutlined,
  ArrowLeftOutlined,
  FireOutlined,
  HomeOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Ad from '../../components/Ad/Ad';
import { apiService } from '../../services/api';
import { AppDetail, AppListItem } from '../../types';
import { constants } from '../../config/constants';
import { trackGAEvent, trackGoogleAdsConversion } from '../../utils/analytics';
import './Download.css';

const { Content } = Layout;
const { Title, Text } = Typography;

const Download: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [app, setApp] = useState<AppDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<'android' | 'ios' | null>(null);
  const [popularApps, setPopularApps] = useState<AppListItem[]>([]);
  const [categoryApps, setCategoryApps] = useState<AppListItem[]>([]);

  useEffect(() => {
    if (id) {
      const appId = parseInt(id);
      trackGoogleAdsConversion('17723879355', 'rx9sCKyKi-UcELvfs4NC', {
        app_id: appId,
      });
      trackGAEvent('Download_detail_conv', {
        app_id: appId,
      });
      loadAppDetail(appId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadPopularApps();
  }, []);

  const loadAppDetail = async (appId: number) => {
    setLoading(true);
    try {
      const response = await apiService.getAppDetail(appId);
      if (response.code === 0) {
        setApp(response.data);
        loadCategoryApps(response.data.category.id);
      }
    } catch (error) {
      console.error('Failed to load app detail:', error);
      message.error('Failed to load app details');
    } finally {
      setLoading(false);
    }
  };

  const loadPopularApps = async () => {
    try {
      const response = await apiService.getPopularApps(8);
      if (response.code === 0) {
        setPopularApps(response.data);
      }
    } catch (error) {
      console.error('Failed to load popular apps:', error);
    }
  };

  const loadCategoryApps = async (categoryId: number) => {
    try {
      const response = await apiService.getApps({
        page: 1,
        per_page: constants.pagination.pageSizeOptions[0],
        category_id: categoryId
      });
      if (response.code === 0) {
        setCategoryApps(response.data.items.filter(item => item.id !== parseInt(id!)));
      }
    } catch (error) {
      console.error('Failed to load category apps:', error);
    }
  };

  const handleDownload = async (platform: 'android' | 'ios') => {
    if (!app) return;

    const downloadUrl = platform === 'android' ? app.android_download_url : app.ios_download_url;

    if (!downloadUrl || downloadUrl.trim() === '') {
      message.info(`${platform === 'android' ? 'Android' : 'iOS'} download link is not available`);
      return;
    }

    setDownloading(platform);

    try {
      await apiService.incrementDownloadCount(parseInt(id!));
      
      setTimeout(() => {
        window.open(downloadUrl, '_blank');
        setDownloading(null);
      }, 500);
    } catch (error) {
      console.error('Failed to increment download count:', error);
      window.open(downloadUrl, '_blank');
      setDownloading(null);
    }
  };

  const handleSearch = (value: string) => {
    if (value.trim()) {
      window.location.href = `/search/${encodeURIComponent(value.trim())}`;
    }
  };

  const handleRandomPlay = () => {
    if (!app) return;

    const availableApps = categoryApps.filter(gameApp => gameApp.id !== app.id);

    if (availableApps.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableApps.length);
      const randomApp = availableApps[randomIndex];
      window.location.href = `/app/${randomApp.id}`;
    } else {
      const availablePopularApps = popularApps.filter(gameApp => gameApp.id !== app.id);
      if (availablePopularApps.length > 0) {
        const randomIndex = Math.floor(Math.random() * availablePopularApps.length);
        const randomApp = availablePopularApps[randomIndex];
        window.location.href = `/app/${randomApp.id}`;
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <Header onSearch={handleSearch} />
        <Content style={{ padding: '50px', textAlign: 'center' }}>
          <Spin size="large" />
        </Content>
        <Footer />
      </Layout>
    );
  }

  if (!app) {
    return (
      <Layout>
        <Header onSearch={handleSearch} />
        <Content style={{ padding: '50px', textAlign: 'center' }}>
          <Title level={3}>App not found</Title>
          <Button href="/">Home</Button>
        </Content>
        <Footer />
      </Layout>
    );
  }

  const isDownloadLinkValid = (url: string | undefined | null): boolean => {
    return !!(url && url.trim() !== '');
  };

  return (
    <Layout className="download-layout">
      <Header onSearch={handleSearch} />

      <Content className="download-content">
        <div className="content-container download-container">
          <Ad positionName="download_page_ad" className="download-ad" />

          <section className="download-info-section">
            <div className="download-info-card">
              <div className="back-button">
                <Button 
                  type="text" 
                  icon={<ArrowLeftOutlined />} 
                  href={`/app/${app.id}`}
                >
                  Back
                </Button>
                <Button 
                  type="text" 
                  icon={<HomeOutlined />} 
                  href="/"
                  className="home-button"
                >
                  Home
                </Button>
              </div>

              <div className="app-info-row">
                <div className="app-logo-section">
                  <img
                    src={app.icon_url || 'https://picsum.photos/id/237/200/200'}
                    alt={app.name}
                    className="app-logo-img"
                  />
                </div>
                
                <div className="app-details">
                  <Title level={2} className="app-name">{app.name}</Title>
                  {app.developer && (
                    <Text type="secondary" className="app-developer">
                      Developer: {app.developer}
                    </Text>
                  )}
                  <div className="app-rating">
                    <Text strong>Rating: {app.rating.toFixed(1)}</Text>
                  </div>
                </div>
              </div>

              <div className="download-section">
                <Title level={4} className="download-title">
                  <DownloadOutlined /> Choose Download Platform
                </Title>
                
                <Row gutter={[24, 16]} className="download-buttons">
                  <Col xs={24} sm={12}>
                    <Button
                      type="primary"
                      size="large"
                      icon={<AndroidOutlined />}
                      className={`download-btn android ${!isDownloadLinkValid(app.android_download_url) ? 'disabled' : ''}`}
                      onClick={() => handleDownload('android')}
                      loading={downloading === 'android'}
                      disabled={!isDownloadLinkValid(app.android_download_url)}
                      block
                    >
                      {downloading === 'android' ? 'Redirecting...' : 'Android Download'}
                    </Button>
                    {!isDownloadLinkValid(app.android_download_url) && (
                      <Text type="secondary" className="download-hint">
                        Android version not available
                      </Text>
                    )}
                  </Col>
                  
                  <Col xs={24} sm={12}>
                    <Button
                      type="primary"
                      size="large"
                      icon={<AppleOutlined />}
                      className={`download-btn ios ${!isDownloadLinkValid(app.ios_download_url) ? 'disabled' : ''}`}
                      onClick={() => handleDownload('ios')}
                      loading={downloading === 'ios'}
                      disabled={!isDownloadLinkValid(app.ios_download_url)}
                      block
                    >
                      {downloading === 'ios' ? 'Redirecting...' : 'iOS Download'}
                    </Button>
                    {!isDownloadLinkValid(app.ios_download_url) && (
                      <Text type="secondary" className="download-hint">
                        iOS version not available
                      </Text>
                    )}
                  </Col>
                </Row>
              </div>

              <div className="app-description">
                <Title level={4}>App Description</Title>
                <div className="description-content">
                  {app.description}
                </div>
              </div>

              <div className="security-notice">
                <Space direction="vertical" size="small">
                  <Text type="secondary">
                    <FireOutlined /> Safe Download Tips:
                  </Text>
                  <Text type="secondary" className="security-text">
                    • We only provide official app store download links
                  </Text>
                  <Text type="secondary" className="security-text">
                    • Please ensure downloading from official channels to avoid security risks
                  </Text>
                  <Text type="secondary" className="security-text">
                    • Please confirm device compatibility before downloading
                  </Text>
                </Space>
              </div>
            </div>
          </section>

          {/* 更多游戏推荐 */}
          <section className="more-games-section">
            <div className="section-header">
              <Title level={4} className="section-title">
                <AppstoreOutlined className="section-icon" />
                More Games
              </Title>
            </div>

            <Row gutter={[16, 16]} className="games-grid">
              {[...popularApps, ...categoryApps].slice(0, 12).map((gameApp) => (
                <Col key={gameApp.id} xs={12} sm={8} md={6} lg={4} xl={4}>
                  <a href={`/app/${gameApp.id}`} className="game-card-link">
                    <div className="game-card game-card-hover">
                      <div className="game-card-image">
                        <img
                          src={gameApp.icon_url || 'https://picsum.photos/300/300'}
                          alt={gameApp.name}
                          className="game-card-img"
                        />
                      </div>
                      <div className="game-card-info">
                        <Title level={5} className="game-card-title" ellipsis>
                          {gameApp.name}
                        </Title>
                        <div className="game-card-rating">
                          <Rate disabled value={4 + Math.random()} allowHalf style={{ fontSize: '12px' }} />
                          <span className="rating-text">{(4 + Math.random()).toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </a>
                </Col>
              ))}
            </Row>

            <div className="random-play-section">
              <Button
                type="primary"
                size="large"
                className="btn-random-play"
                icon={<FireOutlined />}
                onClick={handleRandomPlay}
              >
                Random Play
              </Button>
            </div>
          </section>
        </div>
      </Content>

      <Footer />
    </Layout>
  );
};

export default Download;
